import { personas } from "../lookup"; // Import the personas lookup data
import {
  getArtworkData,
  getEventData,
  getEraData,
  getPersonaData,
} from "./Utils/utils"; // Import utility functions to get specific data

/* 
  This class manages the state of the application, including visitor data, artwork, events, eras, and personas.
  It initializes the state based on provided parameters, assigns values to specific paths in the state object, and extracts asset URLs for preloading.
*/
class StateManager {
  // Constructor initializes the state using the provided parameters
  constructor(params) {
    this.params = params; // Parameters passed during initialization (e.g., from URL or config)
    this.state = this.initializeState(); // Initializes state based on the params
  }

  // Initializes the state object with default structure and mapped values
  initializeState() {
    const state = {
      visitor: {}, // Placeholder for visitor data
      personas, // Stores all persona data
      persona: {}, // Placeholder for a specific persona
      highBpm: {}, // Data related to high BPM artwork
      lowBpm: {}, // Data related to low BPM artwork
      time: {}, // Data related to time-based artwork
      era: {}, // Placeholder for era-related data
      event: {}, // Placeholder for event-related data
    };

    // Maps specific parameters to corresponding state paths
    const mapping = {
      VN: "visitor.name", // Maps visitor name to state
      VID: "visitor.id", // Maps visitor ID to state
      VHAID: "highBpm.artwork", // Maps high BPM artwork ID to state
      VH: "highBpm.value", // Maps high BPM value to state
      VLAID: "lowBpm.artwork", // Maps low BPM artwork ID to state
      VL: "lowBpm.value", // Maps low BPM value to state
      VTAID: "time.artwork", // Maps time artwork ID to state
      VT: "time.value", // Maps time value to state
      VE: "event", // Maps event data to state
      VC: "era", // Maps era data to state
      VP: "persona", // Maps persona data to state
    };

    // Loops over the provided params and assigns values to the state
    for (const key in this.params) {
      if (this.params.hasOwnProperty(key) && mapping[key]) {
        this.assignState(state, mapping[key], this.params[key]); // Calls helper method to assign state based on the mapped path
      }
    }

    return state; // Returns the fully initialized state
  }

  // Helper method to assign values to state at specific paths
  assignState(state, path, value) {
    const keys = path.split("."); // Splits the path into keys (e.g., highBpm.artwork -> ["highBpm", "artwork"])
    const lastKey = keys.pop(); // Extracts the last key (e.g., "artwork")
    let currentState = state; // Begins at the root state

    // Traverse the state object following the path keys
    keys.forEach((key) => {
      if (!currentState[key]) currentState[key] = {}; // Ensures the current key exists in state
      currentState = currentState[key]; // Moves deeper into the state
    });

    // Assigns value to the last key in the path
    switch (lastKey) {
      case "artwork":
        currentState[lastKey] = getArtworkData(value); // Gets and assigns artwork data
        break;
      case "event":
        currentState[lastKey] = getEventData(value); // Gets and assigns event data
        break;
      case "era":
        currentState[lastKey] = getEraData(value); // Gets and assigns era data
        break;
      case "persona":
        currentState[lastKey] = getPersonaData(value); // Gets and assigns persona data
        break;
      default:
        currentState[lastKey] = value; // Assigns the value directly for other keys
    }
  }

  // Extracts asset URLs (images/videos) from the state to be preloaded
  getAssetsFromState() {
    const assets = []; // Array to store asset objects
    const artworkKeys = ["highBpm", "lowBpm", "time"]; // Keys related to artwork assets

    // Loops through each artwork key and adds artwork assets to the list
    artworkKeys.forEach((key) => {
      const artwork = this.state[key]?.artwork; // Retrieves artwork data from state
      if (artwork) {
        assets.push({
          url: `/assets/images/artwork/${artwork.image_filename}`, // Constructs the URL for the artwork image
          type: "image", // Specifies the asset type as image
        });

        if (artwork.image_title) {
          window.dataLayer.push({
            event: "artwork_load",
            artwork_title: artwork.image_title,
            artwork_id: artwork.id,
            artwork_artist: artwork.image_artist,
          });
        }
      }
    });

    // Adds event image asset if available
    if (this.state.event?.image_filename) {
      assets.push({
        url: `/assets/images/artwork/${this.state.event.image_filename}`, // Constructs the URL for the event image
        type: "image", // Specifies the asset type as image
      });

      window.dataLayer.push({
        event: "event_load",
        artwork_title: this.state.event.image_title,
        event_id: this.state.event.id,
        artwork_artist: this.state.event.image_artist,
      });
    }

    // Adds persona video assets if available
    if (this.state.personas) {
      this.state.personas.forEach((persona) => {
        assets.push({
          url: persona.video_filename, // URL for persona video
          type: "video", // Specifies the asset type as video
        });
      });
    }

    // Adds era image asset if available
    if (this.state.era?.image_filename) {
      assets.push({
        url: `/assets/images/${this.state.era.image_filename}`, // Constructs the URL for the era image
        type: "image", // Specifies the asset type as image
      });
    }

    return assets; // Returns the array of asset objects
  }
}

export default StateManager; // Exports the StateManager class
